import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import styles from "./FormikUpload.module.scss";
import RemoveIconGreen from "../../../icons/close.svg";
import RemoveIconYellow from "../../../icons/close-yellow.svg";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../utils/utils";
import { useTranslation } from "react-i18next";
import { Paperclip } from "lucide-react";

const FormikUpload = ({
  greenIcon,
  yellowIcon,
  variant = "inline",
  dropzoneClassName,
  className,
  labelClassName,
  labelContent,
  helperText,
  field,
  form,
  ...props
}) => {
  const { t } = useTranslation();

  const [fileNames, setFileNames] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const sessionToken = localStorage.getItem("session_token");
  const { orgId } = useParams();

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    const newFileNames = [];
    const newFileUrls = [];

    for (const file of files) {
      try {
        // Prepare form data
        const formData = new FormData();
        formData.append("org_id", orgId);
        formData.append("file", file);

        // Send file to the server
        const response = await axiosInstance.post("/s3/file", formData, {
          headers: {
            "session-token": sessionToken,
            "Content-Type": "multipart/form-data",
          },
        });

        // Collect the file name and URL
        newFileNames.push(file.name);
        newFileUrls.push(response.data);
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    }

    // Update the state and form field with the new files
    setFileNames((prevFileNames) => [...prevFileNames, ...newFileNames]);
    form.setFieldValue(field.name, [
      ...(form.values[field.name] || []),
      ...newFileUrls,
    ]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setUploading(false);
  };

  const handleChange = async (event) => {
    const files = Array.from(event.currentTarget.files || []);
    await uploadFiles(files);
  };

  const handleRemove = (index) => {
    const newFileNames = fileNames.filter((_, i) => i !== index);
    const newFileUrls = (form.values[field.name] || []).filter(
      (_, i) => i !== index,
    );

    setFileNames(newFileNames);
    form.setFieldValue(field.name, newFileUrls);

    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset the input value to allow re-uploading
    }
  };

  // useEffect to reset fileNames when form value is cleared
  useEffect(() => {
    if (form.values[field.name].length === 0) {
      setFileNames([]); // Clear the file names
    }
  }, [form.values[field.name]]);

  const renderLabelContent = () =>
    labelContent || (uploading ? t("formikUpload.uploading") : t("formikUpload.chooseFiles"));

  const removeIcon = yellowIcon ? RemoveIconYellow : RemoveIconGreen;

  const renderInlineFileList = () => (
    <div className={styles.fileInfo}>
      {fileNames.map((name, index) => (
        <div key={index} className={styles.fileItem}>
          <span className={styles.fileName}>{name}</span>
          {(greenIcon || yellowIcon) && (
            <img
              src={removeIcon}
              className={styles.removeButton}
              onClick={() => handleRemove(index)}
              alt="Remove"
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderDropzoneFileList = () => {
    if (!fileNames.length) return null;

    return (
      <div className={styles.selectedFiles}>
        <div className={styles.selectedFilesTitle}>
          Selected Files ({fileNames.length})
        </div>
        <div className={styles.selectedFilesList}>
          {fileNames.map((name, index) => (
            <div key={index} className={styles.selectedFileItem}>
              <Paperclip size={16} className={styles.paperclipIcon} />
              <div className={styles.selectedFileName}>{name}</div>
              {(greenIcon || yellowIcon) && (
                <img
                  src={removeIcon}
                  className={styles.removeButtonLarge}
                  onClick={() => handleRemove(index)}
                  alt="Remove"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const uploadWrapper = (
    <div
      className={classNames(styles.wrapper, className, dropzoneClassName, {
        [styles.dragActive]: isDragging,
        [styles.dropzoneVariant]: variant === "dropzone",
      })}
      onDragEnter={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (uploading) return;
        dragCounterRef.current += 1;
        if (event.dataTransfer?.items?.length) {
          setIsDragging(true);
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (uploading) return;
        if (!isDragging) setIsDragging(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (uploading) return;
        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) {
          dragCounterRef.current = 0;
          setIsDragging(false);
        }
      }}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (uploading) return;
        dragCounterRef.current = 0;
        setIsDragging(false);
        const files = Array.from(event.dataTransfer?.files || []);
        void uploadFiles(files);
      }}
    >
      <input
        id={field.name}
        name={field.name}
        type="file"
        ref={fileInputRef}
        onChange={handleChange}
        multiple // Allow multiple files
        {...props}
        className={styles.uploadInput}
        disabled={uploading}
      />
      <label
        htmlFor={field.name}
        className={classNames(styles.uploadLabel, labelClassName)}
      >
        {renderLabelContent()}
      </label>
      {variant === "inline" && renderInlineFileList()}
    </div>
  );

  return (
    <div className={styles.container}>
      {uploadWrapper}
      {variant === "dropzone" && renderDropzoneFileList()}
      {helperText && <div className={styles.helperText}>{helperText}</div>}
      {form.errors[field.name] && form.touched[field.name] && (
        <div className={styles.error}>{form.errors[field.name]}</div>
      )}
    </div>
  );
};

export default FormikUpload;
